import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LuBot, LuLoaderPinwheel, LuMaximize2, LuPin, LuPinOff, LuPlus, LuSend, LuX } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { getAgentDisplayName } from '../../entities/chat-agent';
import {
  ChatSession,
  ChatSessionMessage,
  createSession,
  getProjectSessions,
  getSessionMessages,
} from '../../shared/api/chat-sessions';
import { AIChatDrawerPosition, useAIChat } from '../../shared/contexts/AIChat.context';
import { auth } from '../../shared/firebase/firebase';
import { DashboardChatMessage, useDashboardChat } from '../../shared/hooks/use-dashboard-chat';
import ChatMessageActions from '../chat/ChatMessageActions';
import MarkdownRenderer from '../MarkdownRenderer';

export const AI_CHAT_DRAWER_WIDTH = 420;
export const AI_CHAT_DRAWER_HEIGHT = 380;

/**
 * The drawer must paint above the app chrome (sidebar z-40, mobile top bar z-50)
 * when it is an unpinned overlay, but stay below the sandbox banner (z-[55]),
 * which is intentionally kept above every other surface.
 */
const DRAWER_BACKDROP_Z = 'z-[51]';
const DRAWER_PANEL_Z = 'z-[52]';

const DEFAULT_PROMPTS = [
  'What can you do?',
  'What are my projects biggest risks right now?',
  'Help me plan a sprint for next week',
  'Which team members are overloaded?',
];

type AIChatDrawerProps = {
  /** Width of the app sidebar; keeps docked drawers clear of the menu. */
  sidebarWidth?: number;
  /** Height reserved by a global top banner (sandbox mode). */
  topOffset?: number;
  /** Whether the viewport is wide enough for side docking vs. the mobile bottom sheet. */
  isDesktop?: boolean;
};

export const AIChatDrawer: React.FC<AIChatDrawerProps> = ({ sidebarWidth = 0, topOffset = 0, isDesktop = true }) => {
  const { isOpen, chatParams, closeChat, isPinned, togglePinned, position, setPosition } = useAIChat();
  const { projectId, taskManager, suggestedPrompts, insightText, connectedAgents } = chatParams;
  const effectivePrompts = suggestedPrompts ? [...DEFAULT_PROMPTS, ...suggestedPrompts] : DEFAULT_PROMPTS;
  const navigate = useNavigate();

  const [input, setInput] = useState('');
  const [isExpanding, setIsExpanding] = useState(false);

  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSession, setActiveSession] = useState<ChatSession | null>(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [historyMessages, setHistoryMessages] = useState<ChatSessionMessage[]>([]);

  const { liveMessages, typingText, sendMessage, clearMessages } = useDashboardChat();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const allMessages: DashboardChatMessage[] = [...historyMessages.map((m) => ({ ...m })), ...liveMessages];

  const isDocked = isPinned && isOpen;
  const resolvedPosition: AIChatDrawerPosition = isPinned && !isDesktop ? 'bottom' : position;
  const isRight = resolvedPosition === 'right';
  const isLeft = resolvedPosition === 'left';
  const isBottom = resolvedPosition === 'bottom';

  // Reset and load sessions when drawer opens
  useEffect(() => {
    if (!isOpen) return;
    setSessions([]);
    setActiveSession(null);
    setHistoryMessages([]);
    clearMessages();
    setInput('');

    if (!projectId && !auth.currentUser?.uid) return;
    (async () => {
      setSessionLoading(true);
      try {
        const pId = projectId || `studio-${auth.currentUser?.uid}`;
        let list = await getProjectSessions(pId);
        if (list.length === 0) {
          const created = await createSession(pId);
          list = [created];
        }
        setSessions(list);
        setActiveSession(list[0]);
        const msgs = await getSessionMessages(list[0]._id);
        setHistoryMessages(msgs);
      } catch {
        // Session API not ready yet — chat still works, sessionId just won't be passed
      } finally {
        setSessionLoading(false);
      }
    })();
  }, [isOpen, projectId, clearMessages]);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [allMessages.length, typingText]);

  const handleNewSession = async () => {
    setHistoryMessages([]);
    setActiveSession(null);
    clearMessages();
    if (!projectId) return;
    setSessionLoading(true);
    try {
      const session = await createSession(projectId);
      setSessions((prev) => [session, ...prev]);
      setActiveSession(session);
    } catch {
      // ignore — chat works without a persisted session
    } finally {
      setSessionLoading(false);
    }
  };

  const handleSwitchSession = async (session: ChatSession) => {
    setActiveSession(session);
    setHistoryMessages([]);
    clearMessages();
    try {
      const msgs = await getSessionMessages(session._id);
      setHistoryMessages(msgs);
    } catch {
      // ignore
    }
  };

  const handleExpand = () => {
    if (!activeSession || !projectId) return;
    setIsExpanding(true);
    setTimeout(() => {
      closeChat();
      setIsExpanding(false);
      navigate(`/chat/sessions/${activeSession._id}?projectId=${projectId}`);
    }, 380);
  };

  const handleSend = (text = input) => {
    if (!text.trim()) return;
    sendMessage(text.trim(), {
      projectId,
      taskManager,
      sessionId: activeSession?._id,
      analyticsContext: chatParams.analyticsContext,
      connectedAgents,
    });
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  const useDockedMetrics = isPinned;
  const sideWidth = useDockedMetrics ? `${AI_CHAT_DRAWER_WIDTH}px` : 'min(420px, 100vw)';
  const bottomHeight = useDockedMetrics ? `${AI_CHAT_DRAWER_HEIGHT}px` : 'min(45vh, 420px)';
  const bottomWidth = useDockedMetrics ? `calc(100vw - ${sidebarWidth}px)` : '100vw';

  const openTransform = isOpen
    ? isBottom
      ? `translateX(${useDockedMetrics ? sidebarWidth : 0}px) translateY(0)`
      : isRight
        ? `translateX(calc(100vw - ${sideWidth}))`
        : `translateX(${isDocked ? sidebarWidth : 0}px)`
    : isRight
      ? 'translateX(100vw)'
      : isLeft
        ? 'translateX(-100%)'
        : `translateX(${useDockedMetrics ? sidebarWidth : 0}px) translateY(100%)`;

  const panelStyle: React.CSSProperties = isExpanding
    ? {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        width: '100vw',
        height: '100vh',
        transform: 'translateX(0) translateY(0)',
        transition: 'width 380ms cubic-bezier(0.4, 0, 0.2, 1)',
      }
    : {
        top: isBottom ? undefined : isPinned ? topOffset : 0,
        bottom: 0,
        left: 0,
        right: 'auto',
        width: isBottom ? bottomWidth : sideWidth,
        height: isBottom ? bottomHeight : undefined,
        transform: openTransform,
        transition: 'transform 300ms ease, width 300ms ease, height 300ms ease, top 300ms ease',
      };

  return createPortal(
    <>
      {/* Backdrop — rendered in both modes so pinning fades it in/out instead of popping. */}
      {isOpen && (
        <div
          className={`fixed inset-0 ${DRAWER_BACKDROP_Z} bg-black/60 backdrop-blur-sm`}
          style={{
            transition: 'opacity 380ms ease',
            opacity: isExpanding || isPinned ? 0 : 1,
            pointerEvents: isExpanding || isPinned ? 'none' : 'auto',
          }}
          role="presentation"
          onClick={closeChat}
          onKeyDown={(e) => e.key === 'Escape' && closeChat()}
        />
      )}

      {/* Drawer panel */}
      <div className={`fixed ${DRAWER_PANEL_Z} bg-base-100 shadow-panel flex flex-col`} style={panelStyle}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-base-300 shrink-0 gap-2 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <LuBot className="opacity-70 shrink-0" />
            <span className="font-semibold shrink-0">Ask mFlow</span>
            {activeSession?.title && (
              <span className="text-xs opacity-50 truncate hidden sm:block">{activeSession.title}</span>
            )}
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {sessions.length > 1 && (
              <select
                className="select select-bordered select-xs bg-base-100 hidden sm:block max-w-[120px]"
                value={activeSession?._id ?? ''}
                onChange={(e) => {
                  const s = sessions.find((x) => x._id === e.target.value);
                  if (s) handleSwitchSession(s);
                }}
              >
                {sessions.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.title ?? new Date(s.createdAt).toLocaleDateString()}
                  </option>
                ))}
              </select>
            )}
            <button
              className="btn btn-ghost btn-xs gap-1"
              onClick={handleNewSession}
              disabled={sessionLoading}
              title="New session"
            >
              <LuPlus size={14} />
              <span className="hidden sm:inline">New</span>
            </button>
            <button
              className={`btn btn-ghost btn-xs gap-1 ${isPinned ? 'text-primary' : ''}`}
              onClick={togglePinned}
              title={isPinned ? 'Unpin chat (overlay mode)' : 'Pin chat (dock to page)'}
              aria-pressed={isPinned}
            >
              {isPinned ? <LuPinOff size={14} /> : <LuPin size={14} />}
            </button>
            <select
              className="select select-bordered select-xs bg-base-100 max-w-[96px]"
              value={position}
              onChange={(e) => setPosition(e.target.value as AIChatDrawerPosition)}
              title="Chat dock position"
              aria-label="Chat dock position"
            >
              <option value="right">Right</option>
              <option value="left">Left</option>
              <option value="bottom">Bottom</option>
            </select>
            {activeSession && projectId && (
              <button
                className="btn btn-ghost btn-sm btn-circle hidden sm:flex"
                onClick={handleExpand}
                title="Open full screen"
              >
                <LuMaximize2 size={15} />
              </button>
            )}
            <button className="btn btn-ghost btn-sm btn-circle" onClick={closeChat} title="Close">
              <LuX />
            </button>
          </div>
        </div>

        {/* Insight context note */}
        {insightText && (
          <div className="px-4 py-2 bg-base-200 border-b border-base-300 text-xs opacity-70 shrink-0">
            <span className="font-medium opacity-100">Context: </span>
            {insightText.length > 160 ? insightText.slice(0, 160).trimEnd() + '…' : insightText}
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {sessionLoading && allMessages.length === 0 && (
            <div className="flex justify-center py-10">
              <LuLoaderPinwheel className="animate-spin opacity-40" size={22} />
            </div>
          )}

          {!sessionLoading && allMessages.length === 0 && effectivePrompts.length > 0 && (
            <div className="space-y-2 pt-2">
              <div className="text-xs opacity-50">Suggested questions</div>
              <div className="flex flex-col gap-2">
                {effectivePrompts.map((p) => (
                  <button
                    key={p}
                    className="btn btn-outline btn-sm justify-start text-left"
                    onClick={() => handleSend(p)}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          {allMessages.map((msg, i) =>
            msg.isUser ? (
              <div key={i} className="flex justify-end">
                <div className="bg-primary/10 text-sm rounded-xl rounded-br-sm px-3 py-2 max-w-[85%] whitespace-pre-wrap">
                  {msg.text}
                </div>
              </div>
            ) : !msg.hidden ? (
              <div key={i} className="text-sm opacity-90 pr-1">
                <div className="text-xs font-semibold opacity-50 mb-1">{getAgentDisplayName(msg.sender)}</div>
                <MarkdownRenderer markdown={msg.text} />
                {msg.typing && (
                  <span className="inline-block w-1.5 h-3.5 bg-current opacity-60 animate-pulse ml-0.5 align-middle" />
                )}
                {!!msg.actions?.length && <ChatMessageActions actions={msg.actions} />}
              </div>
            ) : null,
          )}

          {typingText && (
            <div className="flex items-center gap-2 text-sm opacity-60">
              <LuLoaderPinwheel className="animate-spin shrink-0" size={14} />
              {typingText}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="px-4 py-3 border-t border-base-300 shrink-0">
          <div className="flex items-end gap-2">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                e.currentTarget.style.height = 'auto';
                e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Ask about this data…"
              rows={1}
              className="textarea textarea-bordered flex-1 bg-base-100 resize-none overflow-hidden text-sm"
            />
            <button className="btn btn-primary btn-sm" onClick={() => handleSend()} disabled={!input.trim()}>
              <LuSend />
            </button>
          </div>
          <div className="text-xs opacity-40 mt-1 hidden sm:block">Enter to send · Shift+Enter for new line</div>
        </div>

        {/* Mobile close button */}
        <div className="sm:hidden border-t border-base-300 px-4 py-3 shrink-0">
          <button className="btn btn-outline w-full" onClick={closeChat}>
            <LuX size={16} />
            Close
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
};
